import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/atoms/Button';
import EmptyState from '../../../components/atoms/EmptyState';
import { Activity } from '../../../types/support';
import { formatActivityDate } from './utils';

interface TicketActivityProps {
  activities: Activity[];
  onOpenChatHistory?: (roomId: string) => void;
}

export const TicketActivity: React.FC<TicketActivityProps> = ({
  activities,
  onOpenChatHistory,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="bg-gray-50/70 border border-gray-100 rounded-2xl p-6 min-w-0"
      data-testid="ticket-activity-container"
    >
      <h3
        className="text-lg font-semibold text-light-text-primary mb-4"
        data-testid="ticket-activity-title"
      >
        {t('support.ticket_activity_label', 'Ticket activity')}
      </h3>

      {activities && activities.length > 0 ? (
        <div className="flex flex-col gap-5 min-w-0">
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              data-testid={`ticket-activity-item-${activity.id}`}
              className={`flex flex-col gap-2 min-w-0 ${
                index !== activities.length - 1
                  ? 'pb-5 border-b border-gray-200/70'
                  : ''
              }`}
            >
              <div className="flex justify-between items-start gap-4 min-w-0">
                <div className="flex-1 min-w-0">
                  <p
                    className="text-gray-500 text-sm break-words"
                    data-testid={`ticket-activity-action-${activity.id}`}
                  >
                    {t('support.action', 'Action')}:{' '}
                    <span className="font-semibold text-gray-900">
                      {activity.action?.name || '---'}
                    </span>
                  </p>
                  <p
                    className="text-gray-700 text-sm mt-1 break-words whitespace-pre-line"
                    data-testid={`ticket-activity-message-${activity.id}`}
                  >
                    {activity.msg || activity.message || '---'}
                  </p>
                </div>
                {(activity.chatRoomId || activity.roomId) && (
                  <Button
                    variant="outline"
                    className="rounded-lg bg-white shrink-0 shadow-sm"
                    data-testid={`ticket-activity-chat-btn-${activity.id}`}
                    onClick={() => {
                      onOpenChatHistory?.(
                        (activity.chatRoomId || activity.roomId)!
                      );
                    }}
                  >
                    {t('support.seeChatHistory', 'See chat history')}
                  </Button>
                )}
              </div>
              <p
                className="text-gray-400 text-xs"
                data-testid={`ticket-activity-date-${activity.id}`}
              >
                {formatActivityDate(activity.createdDate, t)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          className="!bg-transparent !py-4 [&_img]:!w-28 [&_img]:!h-auto"
          imageClassName="!w-28 !h-auto"
          title={t('support.nothingHereYet', "Looks like there's nothing here yet.")}
        />
      )}
    </div>
  );
};

export default TicketActivity;
